import { useEffect, useRef } from 'react'
import { io } from 'socket.io-client'
import { SOCKET_URL } from '../../config'
import { useNotificationStore, type AdminNotification } from '../../store/notificationStore'
import { canPlaySound } from '../../lib/soundThrottle'

const SOUND_MUTE_KEY = 'rapidry_admin_sound_muted'
const SOUND_FILE = '/notification.mp3'

const isMuted = () => {
  try {
    return sessionStorage.getItem(SOUND_MUTE_KEY) === '1'
  } catch {
    return false
  }
}

// Map a socket event + payload to a bell entry (null = ignore).
type Mapper = (d: any) => Omit<AdminNotification, 'id' | 'at' | 'read'> | null

const EVENT_MAP: Record<string, Mapper> = {
  'order:created': (d) => ({
    kind: 'order',
    title: `New order ${d?.order_number || ''}`.trim(),
    body: d?.total != null ? `₹${d.total} • ${d?.status || 'placed'}` : d?.status || 'placed',
    href: '/orders',
  }),
  'order:cancelled': (d) => ({
    kind: 'cancel',
    title: `Order cancelled ${d?.order_number || ''}`.trim(),
    href: '/orders',
  }),
  'order:cash_collected': (d) => ({
    kind: 'cash',
    title: `Cash collected${d?.amount != null ? ` ₹${d.amount}` : ''}`,
    body: d?.order_number ? `Order ${d.order_number}` : undefined,
    href: '/cash',
  }),
  'order:reschedule_requested': (d) => ({
    kind: 'reschedule',
    title: `Reschedule requested ${d?.order_number || ''}`.trim(),
    body: [d?.new_date, d?.new_slot].filter(Boolean).join(' • ') || undefined,
    href: '/orders',
  }),
  'agent:kyc_doc_uploaded': (d) => ({
    kind: 'kyc',
    title: 'New KYC document to review',
    body: d?.doc_type ? String(d.doc_type) : undefined,
    href: '/management',
  }),
}

/**
 * Global, route-agnostic notification feed for the admin dashboard.
 *
 * Holds its OWN Socket.IO connection + dedup set so it never races with the
 * page-level useSocket hook (which shares a module-level deduplicator - two
 * users of that singleton would steal events from each other). Feeds the Topbar
 * bell store and plays a sound on every new notification, on EVERY route.
 *
 * Browser autoplay policy blocks Audio.play() until the user interacts with the
 * page, so we prime a muted play on the first pointer/key event.
 */
export function NotificationProvider() {
  const add = useNotificationStore((s) => s.add)
  const seen = useRef<Set<string>>(new Set())

  // Unlock audio on first user gesture so later play() calls aren't blocked.
  useEffect(() => {
    const unlock = () => {
      try {
        const a = new Audio(SOUND_FILE)
        a.volume = 0
        void a.play().catch(() => {})
      } catch {
        /* ignore */
      }
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
    window.addEventListener('pointerdown', unlock)
    window.addEventListener('keydown', unlock)
    return () => {
      window.removeEventListener('pointerdown', unlock)
      window.removeEventListener('keydown', unlock)
    }
  }, [])

  const playSound = () => {
    if (isMuted()) return
    if (!canPlaySound()) return // shared throttle: one beep / 2s across all sources
    try {
      const a = new Audio(SOUND_FILE)
      a.volume = 0.6
      void a.play().catch(() => {})
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('rapidry_admin_token')
    if (!token) return

    const s = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
    })

    Object.entries(EVENT_MAP).forEach(([event, map]) => {
      s.on(event, (data: any) => {
        const id =
          data?.event_id ||
          `${event}:${data?.order_id || data?.agent_id || ''}:${
            data?.updated_at || data?.collected_at || data?.requested_at || data?.uploaded_at || Date.now()
          }`
        if (seen.current.has(id)) return
        seen.current.add(id)
        if (seen.current.size > 200) {
          seen.current = new Set(Array.from(seen.current).slice(-100))
        }
        const mapped = map(data)
        if (!mapped) return
        add({ id, at: Date.now(), ...mapped })
        playSound()
      })
    })

    return () => {
      s.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [add])

  return null
}

export default NotificationProvider
